import {
  type ColumnDef,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ArrowDown, ArrowUp, ChevronsUpDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

// How many columns deep the tiebreaker stack goes. Clicking a new column makes
// it the primary key and pushes the previous keys down as tiebreakers; beyond
// this depth the oldest key drops off. Keeps the "stable across sequential
// clicks" behavior without an unbounded sort spec.
const MAX_SORT_DEPTH = 4;

interface DataTableProps<T> {
  columns: ColumnDef<T>[];
  data: T[];
  onRowClick?: (row: T) => void;
  isLoading?: boolean;
  /** Enable per-column sorting via clickable headers. Opt-in so existing
   *  tables render unchanged. */
  enableSorting?: boolean;
  /** Initial sort state when sorting is enabled. */
  initialSorting?: SortingState;
  /** Controlled global filter string. When provided, rows are filtered by
   *  tanstack's includesString across all columns. */
  globalFilter?: string;
}

export function DataTable<T>({
  columns,
  data,
  onRowClick,
  isLoading,
  enableSorting = false,
  initialSorting = [],
  globalFilter,
}: DataTableProps<T>) {
  const [sorting, setSorting] = useState<SortingState>(initialSorting);

  // Stable, composable sort: a click makes the column the PRIMARY key while
  // keeping the previously-clicked columns as lower-priority tiebreakers. So
  // "sort A, then sort B" resolves ties within equal B by A — and a re-sort
  // never arbitrarily reshuffles rows that tie on the new key, because the
  // prior order is encoded as explicit tiebreakers rather than left to the
  // (unstable across re-sorts) original row order. Clicking the current
  // primary column just flips its direction.
  const toggleSort = (columnId: string) => {
    setSorting((prev) => {
      const current = prev.find((s) => s.id === columnId);
      if (current && prev[0]?.id === columnId) {
        // Already primary → flip direction, keep the tiebreaker stack intact.
        return [{ id: columnId, desc: !current.desc }, ...prev.slice(1)];
      }
      // New primary (asc). Demote the rest to tiebreakers, drop any stale
      // copy of this column, and cap the depth.
      const rest = prev.filter((s) => s.id !== columnId);
      return [{ id: columnId, desc: false }, ...rest].slice(0, MAX_SORT_DEPTH);
    });
  };

  const table = useReactTable({
    data,
    columns,
    state: {
      ...(enableSorting ? { sorting } : {}),
      ...(globalFilter !== undefined ? { globalFilter } : {}),
    },
    onSortingChange: enableSorting ? setSorting : undefined,
    enableMultiSort: enableSorting,
    // Never auto-clear our tiebreaker stack — direction only toggles.
    enableSortingRemoval: false,
    isMultiSortEvent: () => true,
    getCoreRowModel: getCoreRowModel(),
    ...(enableSorting ? { getSortedRowModel: getSortedRowModel() } : {}),
    ...(globalFilter !== undefined ? { getFilteredRowModel: getFilteredRowModel() } : {}),
  });

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((hg) => (
            <TableRow key={hg.id}>
              {hg.headers.map((header) => {
                const canSort = enableSorting && header.column.getCanSort();
                const sorted = header.column.getIsSorted();
                // 1-based position in the sort spec; shown for tiebreakers so
                // the stacking order is visible.
                const sortIndex = header.column.getSortIndex();
                return (
                  <TableHead key={header.id}>
                    {header.isPlaceholder ? null : canSort ? (
                      <button
                        type="button"
                        onClick={() => toggleSort(header.column.id)}
                        className={cn(
                          "flex items-center gap-1 -ml-1 rounded px-1 py-0.5",
                          "hover:text-foreground transition-colors",
                          sorted ? "text-foreground" : "text-muted-foreground",
                        )}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {sorted === "asc" ? (
                          <ArrowUp className="size-3.5" />
                        ) : sorted === "desc" ? (
                          <ArrowDown className="size-3.5" />
                        ) : (
                          <ChevronsUpDown className="size-3.5 opacity-50" />
                        )}
                        {sorted && sortIndex > 0 ? (
                          <span className="text-[10px] font-medium tabular-nums text-muted-foreground">
                            {sortIndex + 1}
                          </span>
                        ) : null}
                      </button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={columns.length} className="py-8">
                <div className="flex items-center justify-center gap-2 text-muted-foreground">
                  <Loader2 className="size-4 animate-spin" /> Loading…
                </div>
              </TableCell>
            </TableRow>
          ) : table.getRowModel().rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} className="text-center py-8 text-muted-foreground">
                No data
              </TableCell>
            </TableRow>
          ) : (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                onClick={() => onRowClick?.(row.original)}
                className={onRowClick ? "cursor-pointer" : ""}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
